import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { CreateContractInvoiceData } from "../../types/contract-invoice";
import contractInvoiceService from "../services/contract-invoice-service";

const QUERY_KEY = "contract-invoices";

export const useContractInvoicesQuery = (contractId: string, formDateFrom: string, formDateTo: string) => {
	return useQuery({
		queryKey: [QUERY_KEY, "by-contract", contractId, formDateFrom, formDateTo],
		queryFn: () => contractInvoiceService.listByContract(contractId, formDateFrom, formDateTo),
		enabled: !!contractId,
	});
};

export const useContractInvoicesListQuery = (params?: {
	sales_contract_id?: string;
	start_date?: string;
	end_date?: string;
	status?: string;
	collection_status?: string;
	search?: string;
	salesperson_id?: string;
	per_page?: number;
	page?: number;
}) => {
	return useQuery({
		queryKey: [QUERY_KEY, "list", params],
		queryFn: () => contractInvoiceService.list(params),
	});
};

export const useContractInvoiceQuery = (invoiceId: string) => {
	return useQuery({
		queryKey: [QUERY_KEY, invoiceId],
		queryFn: () => contractInvoiceService.findById(invoiceId),
		enabled: !!invoiceId,
	});
};

export const useUpdateCollectionStatusMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			invoiceId,
			data,
		}: {
			invoiceId: string;
			data: {
				collection_status: "pending" | "collected";
				collection_remarks?: string;
				date_collected?: string;
			};
		}) => contractInvoiceService.updateCollectionStatus(invoiceId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: ["sales-contracts"] });
			toast.success("Collection status updated successfully");
		},
		onError: (error: { response?: { data?: { message?: string } } }) => {
			toast.error(error.response?.data?.message || "Failed to update collection status");
		},
	});
};

export const useMarkInvoiceAsPaidMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			invoiceId,
			data,
		}: {
			invoiceId: string;
			data: { payment_method?: string };
		}) => contractInvoiceService.markAsPaid(invoiceId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: ["sales-contracts"] });
			toast.success("Invoice marked as paid successfully");
		},
		onError: (error: { response?: { data?: { message?: string } } }) => {
			toast.error(error.response?.data?.message || "Failed to mark invoice as paid");
		},
	});
};

export const useInvoiceCollectionReportQuery = (year: number, salespersonId?: string) => {
	return useQuery({
		queryKey: [QUERY_KEY, "collection-report", year, salespersonId],
		queryFn: () => contractInvoiceService.getCollectionReport(year, salespersonId),
		refetchInterval: 300000, // Refetch every 5 minutes
	});
};

export const useUpcomingInvoiceDueDatesQuery = (days?: number, salespersonId?: string) => {
	return useQuery({
		queryKey: [QUERY_KEY, "upcoming-due-dates", days, salespersonId],
		queryFn: () => contractInvoiceService.getUpcomingDueDates(days, salespersonId),
		refetchInterval: 300000, // Refetch every 5 minutes
	});
};

export const useCreateContractInvoiceMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateContractInvoiceData) => contractInvoiceService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: ["sales-contracts"] });
			toast.success("Invoice created successfully");
		},
		onError: (error: { response?: { data?: { message?: string } } }) => {
			toast.error(error.response?.data?.message || "Failed to create invoice");
		},
	});
};

export const useUpdateContractInvoiceMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			invoiceId,
			data,
		}: {
			invoiceId: string;
			data: Partial<CreateContractInvoiceData>;
		}) => contractInvoiceService.updateById(invoiceId, data as Record<string, unknown>),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: ["sales-contracts"] });
			toast.success("Invoice updated successfully");
		},
		onError: (error: { response?: { data?: { message?: string } } }) => {
			toast.error(error.response?.data?.message || "Failed to update invoice");
		},
	});
};

export const useDeleteContractInvoiceMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (invoiceId: string) => contractInvoiceService.deleteById(invoiceId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: ["sales-contracts"] });
			toast.success("Invoice deleted successfully");
		},
		onError: (error: { response?: { data?: { message?: string } } }) => {
			console.log(error);
			toast.error(error.response?.data?.message || "Failed to delete invoice");
		},
	});
};
